import { reportHandledException } from '@/lib/error-reporting'
import type { Route } from 'next'
import { getActiveContext } from '@kiao-voice/api-client'

const STORAGE_KEY_PREFIX = 'kiao.billing.threeDsIntent'
const INTENT_TTL_MS = 24 * 60 * 60 * 1000

function storageKey(): string {
  return `${STORAGE_KEY_PREFIX}.${encodeURIComponent(getActiveContext())}`
}

export type BillingThreeDsIntent =
  | { product: 'personal_subscription'; tradeId: number }
  | { product: 'team_subscription'; tradeId: number }
  | { product: 'subscription_renewal'; tradeId: number }
  | { product: 'subscription_upgrade'; tradeId: number; targetPlanCode: 'pro' }
  | { product: 'credit_pack'; tradeId: number }
  | { product: 'wallet_topup'; tradeId: number }
  | { product: 'team_seat_addon'; tradeId: number }

type StoredBillingThreeDsIntent = BillingThreeDsIntent & { expiresAt: number }

function validTradeId(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) > 0
}

function parseIntent(value: unknown): BillingThreeDsIntent | null {
  if (!value || typeof value !== 'object') return null
  const candidate = value as Partial<StoredBillingThreeDsIntent>
  if (!validTradeId(candidate.tradeId)) return null
  if (
    candidate.product === 'personal_subscription' ||
    candidate.product === 'team_subscription' ||
    candidate.product === 'subscription_renewal'
  ) {
    return { product: candidate.product, tradeId: Number(candidate.tradeId) }
  }
  if (
    candidate.product === 'credit_pack' ||
    candidate.product === 'wallet_topup' ||
    candidate.product === 'team_seat_addon'
  ) {
    return { product: candidate.product, tradeId: Number(candidate.tradeId) }
  }
  if (candidate.product === 'subscription_upgrade' && candidate.targetPlanCode === 'pro') {
    return {
      product: 'subscription_upgrade',
      tradeId: Number(candidate.tradeId),
      targetPlanCode: 'pro',
    }
  }
  return null
}

export function billingThreeDsReturnPath(intent: BillingThreeDsIntent): Route {
  return `/billing/3ds-return?billingProduct=${intent.product}&billingTradeId=${intent.tradeId}`
}

export function writeBillingThreeDsIntent(intent: BillingThreeDsIntent): void {
  if (!validTradeId(intent.tradeId)) throw new Error('3DS checkout is missing a valid trade id')
  const stored: StoredBillingThreeDsIntent = {
    ...intent,
    expiresAt: Date.now() + INTENT_TTL_MS,
  }
  try {
    globalThis.sessionStorage?.setItem(storageKey(), JSON.stringify(stored))
  } catch (caughtError) {
    reportHandledException(caughtError, { source: 'features/billing/three-ds-intent.ts' })
    // The API-authored return URL carries the same product + trade correlation.
    // Storage is only a fallback, so privacy-mode SecurityError must never
    // strand a trade after the server has already created it.
  }
}

export function writeSubscriptionUpgradeThreeDsIntent(tradeId: number): void {
  writeBillingThreeDsIntent({ product: 'subscription_upgrade', tradeId, targetPlanCode: 'pro' })
}

function readUrlIntent(search: string): {
  correlationPresent: boolean
  intent: BillingThreeDsIntent | null
} {
  const params = new URLSearchParams(search)
  const correlationPresent = params.has('billingProduct') || params.has('billingTradeId')
  const tradeId = Number(params.get('billingTradeId'))
  if (!validTradeId(tradeId)) return { correlationPresent, intent: null }
  const product = params.get('billingProduct')
  if (
    product === 'personal_subscription' ||
    product === 'team_subscription' ||
    product === 'subscription_renewal'
  ) {
    return { correlationPresent, intent: { product, tradeId } }
  }
  if (product === 'credit_pack' || product === 'wallet_topup' || product === 'team_seat_addon') {
    return { correlationPresent, intent: { product, tradeId } }
  }
  if (product === 'subscription_upgrade') {
    return {
      correlationPresent,
      intent: { product, tradeId, targetPlanCode: 'pro' },
    }
  }
  return { correlationPresent, intent: null }
}

function readStoredIntent(): BillingThreeDsIntent | null {
  let raw: string | null
  try {
    raw = globalThis.sessionStorage?.getItem(storageKey()) ?? null
  } catch (caughtError) {
    reportHandledException(caughtError, { source: 'features/billing/three-ds-intent.ts' })
    return null
  }
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<StoredBillingThreeDsIntent>
    if (typeof parsed.expiresAt !== 'number' || parsed.expiresAt <= Date.now()) {
      clearBillingThreeDsIntent()
      return null
    }
    const intent = parseIntent(parsed)
    if (intent) return intent
  } catch (caughtError) {
    reportHandledException(caughtError, { source: 'features/billing/three-ds-intent.ts' })
    // Malformed storage is cleared below.
  }
  clearBillingThreeDsIntent()
  return null
}

/** Server-authored URL correlation is authoritative and tab-safe. Session
 * storage remains a same-tab fallback for older API responses. */
export function readBillingThreeDsIntent(
  search = typeof globalThis.location === 'undefined' ? '' : globalThis.location.search
): BillingThreeDsIntent | null {
  const urlCorrelation = readUrlIntent(search)
  return urlCorrelation.correlationPresent ? urlCorrelation.intent : readStoredIntent()
}

export function clearBillingThreeDsIntent(): void {
  try {
    globalThis.sessionStorage?.removeItem(storageKey())
    // Never adopt the pre-isolation fallback into whichever workspace happens
    // to be active after an upgrade.
    globalThis.sessionStorage?.removeItem(STORAGE_KEY_PREFIX)
  } catch (caughtError) {
    reportHandledException(caughtError, { source: 'features/billing/three-ds-intent.ts' })
    // Best-effort cleanup; an inaccessible session store is already unusable.
  }
}
